import { useState, FormEvent } from 'react';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';

const CONTACT_URL = 'https://readdy.ai/api/form/d7lg41cb3l9jtdcthqfg';
const NEWSLETTER_URL = 'https://readdy.ai/api/form/d7lg41cb3l9jtdcthqg0';

type ContactForm = { name: string; email: string; company: string; message: string };

function validateContact(f: ContactForm): Partial<Record<keyof ContactForm, string>> {
  const e: Partial<Record<keyof ContactForm, string>> = {};
  if (!f.name.trim()) e.name = 'Please enter your name.';
  if (!f.email.trim()) e.email = 'Please enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = 'Please enter a valid email address.';
  if (!f.message.trim()) e.message = 'Please enter a message.';
  return e;
}

function validateNewsletterEmail(email: string): string | undefined {
  const em = email.trim();
  if (!em) return 'Please enter your email address.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'Please enter a valid email address.';
  return undefined;
}

export default function ContactSection() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  const [contactForm, setContactForm] = useState<ContactForm>({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [contactErrors, setContactErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterError, setNewsletterError] = useState<string>();

  const handleContact = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs = validateContact(contactForm);
    setContactErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setContactStatus('sending');
    const data = new URLSearchParams();
    data.set('name', contactForm.name.trim());
    data.set('email', contactForm.email.trim());
    data.set('company', contactForm.company.trim());
    data.set('message', contactForm.message.trim());
    try {
      const res = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setContactStatus(res.ok ? 'sent' : 'error');
      if (res.ok) {
        setContactForm({ name: '', email: '', company: '', message: '' });
        setContactErrors({});
      }
    } catch {
      setContactStatus('error');
    }
  };

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const msg = validateNewsletterEmail(newsletterEmail);
    setNewsletterError(msg);
    if (msg) return;

    setNewsletterStatus('sending');
    const data = new URLSearchParams();
    data.set('email', newsletterEmail.trim());
    try {
      await fetch(NEWSLETTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setNewsletterStatus('sent');
      setNewsletterEmail('');
      setNewsletterError(undefined);
    } catch {
      setNewsletterStatus('sent');
    }
  };

  const contactInvalid = (k: keyof ContactForm) => !!contactErrors[k];

  return (
    <section id="contact" className="px-5 py-10 bg-[#f8f9fa]">
      <div className="text-center mb-7">
        <span className="inline-block bg-green-50 text-green-600 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full border border-green-100 mb-3">
          Contact Us
        </span>
        <h2 className="text-gray-800 font-bold text-2xl leading-tight mb-2">
          Let&apos;s Talk About<br />Your Business
        </h2>
        <p className="text-gray-400 text-sm">Our team responds within 2 hours on business days.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { icon: 'ri-phone-line',     color: 'text-green-600',  bg: 'bg-green-50',  label: 'Phone',  value: '+1 (888) 555-0199' },
          { icon: 'ri-mail-line',      color: 'text-sky-500',    bg: 'bg-sky-50',    label: 'Email',  value: 'hello@greenflow.io' },
          { icon: 'ri-map-pin-2-line', color: 'text-teal-500',   bg: 'bg-teal-50',   label: 'Office', value: '340 Pine St, SF, CA' },
          { icon: 'ri-time-line',      color: 'text-emerald-500',bg: 'bg-emerald-50',label: 'Hours',  value: 'Mon–Fri 9am–6pm' },
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-3.5">
            <div className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center mb-2`}>
              <i className={`${item.icon} ${item.color}`}></i>
            </div>
            <div className="text-gray-400 text-[10px] mb-0.5">{item.label}</div>
            <div className="text-gray-700 text-xs font-semibold leading-tight">{item.value}</div>
          </div>
        ))}
      </div>

      {/* Contact form */}
      {contactStatus === 'sent' ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-check-line text-green-600 text-2xl"></i>
          </div>
          <div className="text-green-700 font-semibold text-sm">Message Sent!</div>
          <div className="text-green-500 text-xs mt-1">We&apos;ll get back to you within 2 hours.</div>
        </div>
      ) : (
        <form
          lang="en"
          noValidate
          data-readdy-form
          id="contact-form"
          onSubmit={handleContact}
          className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4 mb-6"
        >
          <div className="text-gray-800 font-semibold text-sm mb-1">Send Us a Message</div>
          <div>
            <label htmlFor="contact-name" className="text-xs text-gray-500 font-normal mb-1.5 block">Your Name</label>
            <input
              id="contact-name"
              name="name"
              type="text"
              value={contactForm.name}
              onChange={(e) => {
                setContactForm((p) => ({ ...p, name: e.target.value }));
                setContactErrors((er) => ({ ...er, name: undefined }));
              }}
              placeholder="John Smith"
              aria-invalid={contactInvalid('name')}
              aria-describedby={contactErrors.name ? 'contact-name-error' : undefined}
              className={`w-full h-10 bg-white border rounded-lg px-3 text-sm text-gray-800 outline-none focus:border-green-500 transition-colors placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                contactErrors.name ? 'border-red-300 bg-red-50/35' : 'border-gray-300'
              }`}
            />
            <InlineFieldError id="contact-name-error" message={contactErrors.name} />
          </div>
          <div>
            <label htmlFor="contact-email" className="text-xs text-gray-500 font-normal mb-1.5 block">Email Address</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              autoComplete="email"
              value={contactForm.email}
              onChange={(e) => {
                setContactForm((p) => ({ ...p, email: e.target.value }));
                setContactErrors((er) => ({ ...er, email: undefined }));
              }}
              placeholder="john@company.com"
              aria-invalid={contactInvalid('email')}
              aria-describedby={contactErrors.email ? 'contact-email-error' : undefined}
              className={`w-full h-10 bg-white border rounded-lg px-3 text-sm text-gray-800 outline-none focus:border-green-500 transition-colors placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                contactErrors.email ? 'border-red-300 bg-red-50/35' : 'border-gray-300'
              }`}
            />
            <InlineFieldError id="contact-email-error" message={contactErrors.email} />
          </div>
          <div>
            <label htmlFor="contact-company" className="text-xs text-gray-500 font-normal mb-1.5 block">Company</label>
            <input
              id="contact-company"
              name="company"
              type="text"
              value={contactForm.company}
              onChange={(e) => setContactForm((p) => ({ ...p, company: e.target.value }))}
              placeholder="Your Company Name"
              className="w-full h-10 bg-white border border-gray-300 rounded-lg px-3 text-sm text-gray-800 outline-none focus:border-green-500 transition-colors placeholder:font-normal placeholder:text-xs placeholder:text-gray-400"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="text-xs text-gray-500 font-normal mb-1.5 block">Message</label>
            <textarea
              id="contact-message"
              name="message"
              rows={4}
              maxLength={500}
              value={contactForm.message}
              onChange={(e) => {
                setContactForm((p) => ({ ...p, message: e.target.value }));
                setContactErrors((er) => ({ ...er, message: undefined }));
              }}
              placeholder="Tell us about your cleaning service needs..."
              aria-invalid={contactInvalid('message')}
              aria-describedby={contactErrors.message ? 'contact-message-error' : undefined}
              className={`w-full bg-white border rounded-lg px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-green-500 transition-colors placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 resize-none ${
                contactErrors.message ? 'border-red-300 bg-red-50/35' : 'border-gray-300'
              }`}
            />
            <InlineFieldError id="contact-message-error" message={contactErrors.message} />
          </div>
          <FormSummaryMessage message={contactStatus === 'error' ? 'Something went wrong. Please try again.' : undefined} />
          <button
            type="submit"
            disabled={contactStatus === 'sending'}
            className="w-full h-11 bg-green-600 text-white font-normal text-sm rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center"
          >
            {contactStatus === 'sending' ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      )}

      {/* Newsletter */}
      <div className="bg-green-600 rounded-2xl p-5">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-mail-send-line text-white text-sm"></i>
          </div>
          <div className="text-white font-bold text-sm">Stay in the Loop</div>
        </div>
        <p className="text-green-100 text-xs mb-4 leading-relaxed">
          Get product updates, green cleaning tips, and industry insights. No spam, unsubscribe anytime.
        </p>
        {newsletterStatus === 'sent' ? (
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <i className="ri-check-double-line text-white text-lg block mb-1"></i>
            <span className="text-white text-xs font-semibold">You&apos;re subscribed!</span>
          </div>
        ) : (
          <form
            lang="en"
            noValidate
            data-readdy-form
            id="newsletter-form"
            onSubmit={handleNewsletter}
            className="flex flex-col gap-2"
          >
            <div className="flex gap-2">
              <input
                id="newsletter-email"
                name="email"
                type="email"
                autoComplete="email"
                value={newsletterEmail}
                onChange={(e) => {
                  setNewsletterEmail(e.target.value);
                  setNewsletterError(undefined);
                }}
                placeholder="Your email address"
                aria-invalid={!!newsletterError}
                aria-describedby={newsletterError ? 'newsletter-email-error' : undefined}
                className={`flex-1 h-10 bg-white rounded-lg px-3 text-sm text-gray-800 outline-none placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 min-w-0 border ${
                  newsletterError ? 'border-red-300 bg-red-50/35' : 'border-transparent'
                }`}
              />
              <button
                type="submit"
                disabled={newsletterStatus === 'sending'}
                className="bg-gray-900 text-white text-sm font-normal px-4 h-11 rounded-lg whitespace-nowrap cursor-pointer flex-shrink-0 disabled:opacity-60"
              >
                {newsletterStatus === 'sending' ? '...' : 'Subscribe'}
              </button>
            </div>
            <InlineFieldError id="newsletter-email-error" message={newsletterError} />
          </form>
        )}
      </div>
    </section>
  );
}
